import { useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import DateInput from '@/components/DateInput'

type EditCaseDialogProps = {
  initialName: string
  initialDescription: string | null
  initialExpiresOn: string | null
  saving?: boolean
  error?: string | null
  onCancel: () => void
  onConfirm: (values: {
    name: string
    description: string | null
    expires_on: string | null
  }) => void
}

export default function EditCaseDialog({
  initialName,
  initialDescription,
  initialExpiresOn,
  saving = false,
  error = null,
  onCancel,
  onConfirm,
}: EditCaseDialogProps) {
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription ?? '')
  const [expiresOn, setExpiresOn] = useState(initialExpiresOn ?? '')
  const [noExpiration, setNoExpiration] = useState(!initialExpiresOn)

  const handleSubmit = () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      return
    }
    onConfirm({
      name: trimmedName,
      description: description.trim() ? description.trim() : null,
      expires_on: noExpiration ? null : expiresOn || null,
    })
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-case-title"
        className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="edit-case-title" className="text-sm font-semibold text-gray-900">
          Edit case settings
        </h3>
        {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
        <label className="mt-4 block text-sm text-gray-700">
          Case name
          <input
            type="text"
            className="input mt-1 w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </label>
        <label className="mt-3 block text-sm text-gray-700">
          Description
          <textarea
            className="input mt-1 min-h-[4rem] w-full resize-y"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <div className="mt-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={noExpiration}
              onChange={(e) => {
                setNoExpiration(e.target.checked)
                if (e.target.checked) {
                  setExpiresOn('')
                }
              }}
            />
            No expiration
          </label>
          {!noExpiration ? (
            <label className="mt-2 block text-sm text-gray-700">
              Expires on
              <DateInput
                className="input mt-1 w-full"
                value={expiresOn}
                onChange={setExpiresOn}
                dateFormat="iso"
                aria-label="Case expiration date"
              />
            </label>
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="md" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!name.trim() || saving}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}
